import * as React from 'react';
// @ts-expect-error
import {components} from 'react-select';
// @ts-expect-error
import type {ControlProps, OptionProps} from 'react-select';

import {EntitySelectRow} from 'shells/entity/entity-select-row';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import type {CreateSelectSize} from 'shells/select-inline/select-inline-creatable';

import {ControlWithIcon} from './control-with-icon';
import {GraphQLSelectCreatable} from './graphql-select-creatable';
import type {Option} from './graphql-select-creatable';
import {useCreateAccount} from './hooks/use-create-account';
import {useGetAccounts} from './hooks/use-get-accounts';

type Props = {
    selectedValues: Option[];
    onChange: (values: Option | Option[]) => any;
    onClearValue?: () => void;
    isMulti: boolean;
    clearable?: boolean;
    noIcon?: boolean;
    size?: CreateSelectSize;
    lightPlaceholderText?: boolean;
    placeholder?: string;
    formatCreateLabel?: (inputValue: string) => string;
    persistTextOnBlur?: boolean;
    disabled?: boolean;
    onCreate?: (newValue: string) => void;
};

export const GraphQLSelectAccounts = (props: Props) => {
    const {
        noIcon,
        size = 'normal',
        formatCreateLabel = (inputValue) => `Create new company named '${inputValue}'`,
    } = props;
    const {fetchMore, isLoading} = useGetAccounts();
    const {createAccount} = useCreateAccount();

    const handleCreate = (newAccount: string) => {
        // Immediately add a temp account to the list of selected values
        const tempAccount = {id: newAccount, name: newAccount} as const;
        if (props.isMulti && Array.isArray(props.selectedValues)) {
            props.onChange([...props.selectedValues, tempAccount]);
        } else {
            props.onChange(tempAccount);
        }

        createAccount({name: newAccount}).then((result) => {
            const createdAccount =
                result.data && result.data.accountCreate && result.data.accountCreate.account;
            const createdAccountValue = createdAccount
                ? {id: createdAccount.id, name: createdAccount.name}
                : null;

            // Add the real created account to the list of selected values
            if (createdAccountValue) {
                if (props.isMulti && Array.isArray(props.selectedValues)) {
                    props.onChange([...props.selectedValues, createdAccountValue]);
                } else {
                    props.onChange(createdAccountValue);
                }
            }
        });
    };

    const handleLoadOptions = (search?: string | null) => {
        return fetchMore(search).then(({data}) => {
            const quickResults = data.search ? data.search.quickResults : null;

            let accountResults = quickResults ? quickResults.accountResults : [];

            if (accountResults.length === 0) {
                const recentResults =
                    quickResults && quickResults.recentResults ? quickResults.recentResults : [];
                if (recentResults && recentResults.length > 0) {
                    accountResults = recentResults;
                }
            }

            return Promise.resolve(accountResults);
        });
    };

    const ControlComponent = React.useMemo(
        () =>
            ({children, ...controlProps}: ControlProps) => (
                <components.Control {...controlProps}>
                    {noIcon ? (
                        children
                    ) : (
                        <ControlWithIcon icon={AccountIcon}>{children}</ControlWithIcon>
                    )}
                </components.Control>
            ),
        [noIcon]
    );

    const OptionComponent = React.useMemo(
        () => (optionProps: OptionProps) => {
            // This is a hack to get around limitations in our old version of flow
            const spreadableProps: OptionProps = optionProps as any;

            if (optionProps.data.__isNew__) {
                return (
                    <components.Option {...spreadableProps}>
                        {optionProps.data.name}
                    </components.Option>
                );
            }

            const relatedName =
                optionProps.data.contacts &&
                optionProps.data.contacts.edges &&
                optionProps.data.contacts.edges[0]
                    ? optionProps.data.contacts.edges[0].node.name
                    : undefined;

            const region =
                optionProps.data.postalAddresses &&
                optionProps.data.postalAddresses.primary &&
                optionProps.data.postalAddresses.primary.value &&
                optionProps.data.postalAddresses.primary.value.region
                    ? optionProps.data.postalAddresses.primary.value.region
                    : '';

            return (
                <components.Option {...spreadableProps}>
                    <EntitySelectRow
                        entity={{
                            id: optionProps.data.id,
                            type: 'accounts',
                            value: optionProps.data.name || 'Unnamed company',
                            description: '',
                            tertiaryInfo: region,
                            avatarUrl: optionProps.data.avatarUrl,
                            initials: optionProps.data.initials,
                            secondaryInfo: relatedName ? (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: relatedName,
                                    }}
                                />
                            ) : undefined,
                        }}
                        size={size}
                    />
                </components.Option>
            );
        },
        [size]
    );

    const placeholder =
        props.placeholder ||
        (props.isMulti ? 'Select or create companies' : 'Select or create a company');

    return (
        <GraphQLSelectCreatable
            placeholder={placeholder}
            value={props.selectedValues}
            multi={props.isMulti}
            onClearValue={props.onClearValue}
            loadOptions={handleLoadOptions}
            onChange={props.onChange}
            onBlur={() => {
                props.onChange(props.selectedValues);
            }}
            noOptionsMessage={({inputValue}) => {
                if (isLoading) {
                    return 'Getting recent results…';
                }
                if (inputValue.length) {
                    return `No companies found matching '${inputValue}'`;
                }
                if (inputValue.length === 0) {
                    return 'No recently viewed companies. Type to search all companies…';
                }

                return null;
            }}
            // @ts-expect-error
            formatCreateLabel={formatCreateLabel}
            onCreate={props.onCreate ? props.onCreate : handleCreate}
            components={{Control: ControlComponent, Option: OptionComponent}}
            labelKey='name'
            valueKey='id'
            clearable={props.clearable}
            size={props.size}
            lightPlaceholderText={props.lightPlaceholderText}
            persistTextOnBlur={props.persistTextOnBlur}
            disabled={props.disabled}
        />
    );
};
